import { ANY_RECORD_PREFIX } from "./types";
import type { ConditionNode, FilterFieldType, FilterNode, FilterOperator, FilterSchema, FilterTree, GeoOrigin, GeoValue, GroupNode, Quantifier, RelatedNode, SchemaField } from "./types";

let counter = 0;
export function newNodeId(prefix = "n"): string {
    counter += 1;
    return `${prefix}${Date.now().toString(36)}${counter.toString(36)}`;
}

export function emptyGroup(conjunction: "and" | "or" = "and", id = newNodeId("g")): GroupNode {
    return { type: "group", id, conjunction, children: [] };
}

export function emptyTree(): FilterTree {
    return { version: 1, root: emptyGroup("and", "root") };
}

export function newCondition(field = "", operator: FilterOperator | "" = ""): ConditionNode {
    return { type: "condition", id: newNodeId("c"), field, operator, value: null };
}

export function newRelated(relation: string, quantifier: Quantifier = "any"): RelatedNode {
    return { type: "related", id: newNodeId("r"), relation, quantifier, filter: emptyGroup() };
}

export function newGroup(): GroupNode {
    return { ...emptyGroup("or"), children: [newCondition()] };
}

/**
 * Whether the side panel can edit the tree: conditions only, combined by one and / or. Related
 * records, nested groups, formulas and conditions read back from older filters need the advanced
 * editor.
 */
export function isSimpleTree(tree?: FilterTree | null): boolean {
    // A condition compared with a formula is edited in the advanced editor only.
    return !tree || tree.root.children.every(child => child.type === "condition" && child.formula == null);
}

export function isTreeEmpty(tree?: FilterTree | null): boolean {
    return !tree || tree.root.children.length === 0;
}

/** The tree as the editor needs it: every related block has a group to add conditions to. */
export function normalizeTree(tree: FilterTree): FilterTree {
    const visit = (node: FilterNode): FilterNode => {
        if (node.type === "group") return { ...node, children: node.children.map(visit) };
        if (node.type === "related") return { ...node, filter: node.filter ? (visit(node.filter) as GroupNode) : emptyGroup() };
        return node;
    };
    return { ...tree, root: visit(tree.root) as GroupNode };
}

/** Replace the node `id` (anywhere in the tree) with `update(node)`; `null` removes it. */
export function updateNode(tree: FilterTree, id: string, update: (node: FilterNode) => FilterNode | null): FilterTree {
    const visit = (node: FilterNode): FilterNode | null => {
        if (node.id === id) return update(node);
        if (node.type === "group") {
            const children = node.children.map(visit).filter((child): child is FilterNode => child !== null);
            return { ...node, children };
        }
        if (node.type === "related" && node.filter) {
            const filter = visit(node.filter);
            return { ...node, filter: filter && filter.type === "group" ? filter : emptyGroup() };
        }
        return node;
    };
    const root = visit(tree.root);
    return { ...tree, root: root && root.type === "group" ? root : emptyGroup("and", "root") };
}

export function removeNode(tree: FilterTree, id: string): FilterTree {
    return updateNode(tree, id, () => null);
}

export function appendChild(tree: FilterTree, groupId: string, child: FilterNode): FilterTree {
    return updateNode(tree, groupId, node => (node.type === "group" ? { ...node, children: [...node.children, child] } : node));
}

/**
 * Combine the row `index` of `group` with the rows above it by `conjunction`. A group has one
 * conjunction, so a row that differs from it wraps the rows above in a group of their own:
 * « A or B » then « and C » reads « (A or B) and C ». The first two rows just switch the group.
 */
export function setRowConjunction(group: GroupNode, index: number, conjunction: "and" | "or"): GroupNode {
    if (conjunction === group.conjunction) return group;
    if (index <= 1) return { ...group, conjunction };
    const above: GroupNode = { type: "group", id: newNodeId("g"), conjunction: group.conjunction, children: group.children.slice(0, index) };
    return { ...group, conjunction, children: [above, ...group.children.slice(index)] };
}

export function countConditions(node: FilterNode | null | undefined): number {
    if (!node) return 0;
    if (node.type === "group") return node.children.reduce((total, child) => total + countConditions(child), 0);
    if (node.type === "related") return 1 + countConditions(node.filter ?? null);
    return 1;
}

/** Operators that take no value. */
export const VALUELESS: ReadonlySet<string> = new Set(["is_empty", "is_not_empty", "is_true", "is_false"]);

export function defaultOperator(type: FilterFieldType, operators: FilterOperator[]): FilterOperator {
    const preferred: Record<FilterFieldType, FilterOperator> = {
        text: "contains",
        number: "equals",
        boolean: "is_true",
        date: "on",
        datetime: "on",
        choice: "in",
        picklist: "in",
        record: "in",
        geo: "within_km"
    };
    return operators.includes(preferred[type]) ? preferred[type] : operators[0];
}

/** Whether a condition is filled enough to filter (an incomplete one is flagged, not sent). */
export function isConditionComplete(node: ConditionNode): boolean {
    if (!node.field || !node.operator) return false;
    if (VALUELESS.has(node.operator)) return true;
    // A formula is only kept once the editor found it valid.
    if (node.formula != null) return node.formula.trim() !== "";
    if (DISTANCE_OPERATORS.has(node.operator)) return isGeoValueComplete(geoValueOf(node.value));
    const values = (node.value ?? []).filter(value => value !== null && value !== undefined && String(value).trim() !== "");
    return node.operator === "between" ? values.length === 2 : values.length > 0;
}

/** Ids of the conditions that are not filled yet. */
export function incompleteNodeIds(node: FilterNode | null | undefined, ids: string[] = []): string[] {
    if (!node) return ids;
    if (node.type === "condition" && !isConditionComplete(node)) ids.push(node.id);
    if (node.type === "group") node.children.forEach(child => incompleteNodeIds(child, ids));
    if (node.type === "related") {
        if (COUNTED.has(node.quantifier) && !(Number(node.count) >= 1)) ids.push(node.id);
        incompleteNodeIds(node.filter ?? null, ids);
    }
    return ids;
}

/** The tree without its empty groups and incomplete conditions: what is worth sending. */
export function pruneTree(tree: FilterTree): FilterTree {
    const visit = (node: FilterNode): FilterNode | null => {
        if (node.type === "condition") return isConditionComplete(node) ? node : null;
        if (node.type === "group") {
            const children = node.children.map(visit).filter((child): child is FilterNode => child !== null);
            return children.length ? { ...node, children } : null;
        }
        if (node.type === "related") {
            const filter = node.filter ? visit(node.filter) : null;
            return { ...node, filter: filter && filter.type === "group" ? filter : null };
        }
        return node;
    };
    const root = visit(tree.root);
    return { version: 1, root: root && root.type === "group" ? { ...root, id: "root" } : emptyGroup("and", "root") };
}

/** A stable form of a tree for « has it changed? »: no ids, no hash, no display labels. */
export function treeSignature(tree?: FilterTree | null): string {
    if (!tree) return treeSignature(emptyTree());
    const strip = (node: FilterNode): unknown => {
        if (node.type === "group") return { t: "g", c: node.conjunction, k: node.children.map(strip) };
        if (node.type === "related") return { t: "r", r: node.relation, q: node.quantifier, n: node.count ?? null, f: node.filter ? strip(node.filter) : null };
        if (node.type === "drq") return { t: "q", q: node.query };
        if (node.formula != null) return { t: "c", f: node.field, o: node.operator, x: node.formula };
        return { t: "c", f: node.field, o: node.operator, v: node.value ?? null };
    };
    return JSON.stringify(strip(pruneTree(tree).root));
}

export function areTreesEqual(a?: FilterTree | null, b?: FilterTree | null): boolean {
    return treeSignature(a) === treeSignature(b);
}

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
    contains: "contains",
    not_contains: "does not contain",
    equals: "equals",
    not_equals: "does not equal",
    starts_with: "starts with",
    ends_with: "ends with",
    in: "is any of",
    not_in: "is none of",
    gt: "greater than",
    gte: "greater or equal",
    lt: "less than",
    lte: "less or equal",
    between: "between",
    is_true: "is true",
    is_false: "is false",
    on: "is",
    not_on: "is not",
    before: "before",
    after: "after",
    on_or_before: "on or before",
    on_or_after: "on or after",
    within_km: "is within",
    beyond_km: "is farther than",
    is_empty: "is empty",
    is_not_empty: "is not empty"
};

export const QUANTIFIER_LABELS: Record<Quantifier, string> = {
    any: "Has at least one",
    none: "Has no",
    all: "Has only",
    at_least: "Has at least",
    fewer_than: "Has fewer than"
};

/** The same, for a block over every record of an object: « There is at least one opportunity ». */
export const ANY_RECORD_QUANTIFIER_LABELS: Record<Quantifier, string> = {
    any: "There is at least one",
    none: "There is no",
    all: "Every",
    at_least: "There are at least",
    fewer_than: "There are fewer than"
};

/** Quantifiers that count the related records. */
export const COUNTED: ReadonlySet<string> = new Set(["at_least", "fewer_than"]);

export function operatorsFor(field: Pick<SchemaField, "operators">): FilterOperator[] {
    return field.operators;
}

/**
 * A formula as a summary reads it: `ƒx Created date + 10 days` (each field by its own label,
 * without its object and path).
 */
export function formulaSummary(label: string): string {
    return `ƒx ${label.replace(/\[([^\]]*)\]/g, (_, content: string) => content.split(".").pop()?.trim() || content)}`;
}

/** « a, b or c » / « a and b » for a short summary of values. */
export function joinValues(values: string[], last: "or" | "and" = "or"): string {
    if (values.length <= 1) return values.join("");
    return `${values.slice(0, -1).join(", ")} ${last} ${values[values.length - 1]}`;
}

export const FIELD_TYPE_LABELS: Record<string, string> = {
    text: "Text",
    number: "Number",
    boolean: "Yes / no",
    date: "Date",
    datetime: "Date & time",
    choice: "Choice",
    picklist: "Choice",
    record: "Record",
    geo: "Location"
};

const fold = (text: string) =>
    text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

/** `cmdk` filter: every word typed appears in the item (no fuzzy « opport » → « Organism part »). */
export function containsFilter(value: string, search: string): number {
    const haystack = fold(value);
    return fold(search)
        .split(/\s+/)
        .filter(Boolean)
        .every(word => haystack.includes(word))
        ? 1
        : 0;
}

/** `registration__email` → « Registration › Email », before (or without) the schema labels. */
export function humanizePath(path: string): string {
    return path
        .split("__")
        .map(segment => segment.replace(/_/g, " ").replace(/^\w/, letter => letter.toUpperCase()))
        .join(" › ");
}

/** Operators that measure a distance from an origin (`geo` fields). */
export const DISTANCE_OPERATORS: ReadonlySet<string> = new Set(["within_km", "beyond_km"]);

/** The distance a `geo` condition holds: the only item of its value. */
export function geoValueOf(value: unknown[] | null | undefined): GeoValue {
    const first = value?.[0];
    return first && typeof first === "object" ? (first as GeoValue) : { radius_km: null, origin: null };
}

export function isGeoValueComplete(value: GeoValue): boolean {
    const radius = Number(String(value.radius_km ?? "").replace(",", "."));
    return Number.isFinite(radius) && radius > 0 && Boolean(value.origin);
}

export function originLabel(origin: GeoOrigin | null | undefined): string {
    if (!origin) return "";
    if (origin.label) return origin.label;
    if (origin.type === "point") return `${origin.lat.toFixed(4)}, ${origin.lng.toFixed(4)}`;
    return origin.type === "record" ? `#${origin.id}` : origin.point;
}

/** « 50 km of Lyon » */
export function geoValueSummary(value: GeoValue): string {
    const radius = String(value.radius_km ?? "").replace(".", ",");
    if (!value.origin) return `${radius} km`;
    const label = originLabel(value.origin);
    // « of the registration above › Location »: a role reads mid-sentence.
    return `${radius} km of ${value.origin.type === "field" ? label.charAt(0).toLowerCase() + label.slice(1) : label}`;
}

/**
 * How a related block reads: « Has at least one Registrations », or for a block over every record
 * of an object, « There is at least one opportunity » / « There are fewer than 10 registrations ».
 */
export function relatedPhrase(node: RelatedNode, relationLabel: string, target?: Pick<FilterSchema, "name" | "name_plural"> | null): { quantifier: string; label: string } {
    if (!node.relation.startsWith(ANY_RECORD_PREFIX)) return { quantifier: QUANTIFIER_LABELS[node.quantifier], label: relationLabel };
    const counted = COUNTED.has(node.quantifier);
    const name = (counted ? target?.name_plural : target?.name)?.toLowerCase() ?? relationLabel;
    return { quantifier: ANY_RECORD_QUANTIFIER_LABELS[node.quantifier], label: name };
}
